<template>
  <transition-group
    name="sos-banner"
    tag="div"
    class="sos-banner-stack"
  >
    <div
      v-for="(call, idx) in bannerQueue"
      :key="call._id || idx"
      class="sos-banner"
      :style="{ top: (56 + idx * 76) + 'px' }"
      @mouseenter="pauseAutoDismiss(call._id)"
      @mouseleave="resumeAutoDismiss(call._id)"
    >
      <div class="sos-banner__icon">
        <v-icon dark size="22">mdi-phone-alert</v-icon>
      </div>
      <div class="sos-banner__body">
        <div class="sos-banner__title">
          {{ callTitle(call) }}
          <span class="sos-banner__meta">· {{ callType(call) }} · {{ relativeTime(call) }}</span>
        </div>
        <div class="sos-banner__subtitle">
          {{ callLocation(call) }}
        </div>
      </div>
      <div class="sos-banner__actions">
        <v-btn
          color="white"
          light
          x-small
          class="mr-1"
          @click="atender(call)"
        >
          <v-icon left x-small>mdi-phone</v-icon>
          Atender
        </v-btn>
        <v-btn
          text
          dark
          x-small
          @click="descartar(call)"
        >
          Descartar
        </v-btn>
      </div>
    </div>
  </transition-group>
</template>

<script>
import { mapGetters, mapState } from 'vuex'

// Auto-descarte del banner SOS flotante: tras este tiempo sin interacción, el
// banner se oculta (sale del bannerQueue). La LLAMADA NO se pierde — sigue en
// el sidebar SOS. Pausa al hover. Más largo que PanicBanner (5s) por criticidad.
const AUTO_DISMISS_MS = 30000
// No re-mostramos como banner llamadas más viejas que esto (sobre todo las que
// quedaron persistidas en localStorage tras un reload — el caso "hace 2h").
const STALE_MS = 5 * 60 * 1000

export default {
  name: 'SOSBanner',

  // MapV2.vue expone sosCallServiceGetter (función) porque Vue 2 evalúa
  // provide() antes de created(), cuando this.sosCallService aún es null.
  // El computed sosCallService lee el valor fresh cada vez.
  inject: ['sosCallServiceGetter'],

  data() {
    return {
      nowTick: Date.now(),
      tickInterval: null,
    }
  },

  computed: {
    ...mapGetters('sos-call-v2', ['getBannerQueue']),
    ...mapState('map-v2', ['bannerAlertTypes']),
    bannerQueue() {
      // Toggle del toolbar: si sos_call esta desactivado, no mostramos el
      // banner flotante. Las llamadas siguen llegando al sos-call-service
      // y quedan visibles en el sidebar y el sidebar SOS.
      if (this.bannerAlertTypes && this.bannerAlertTypes.sos_call === false) return []
      // Filtra llamadas viejas: no re-mostramos banners de hace mucho (sobre
      // todo los que persistieron en localStorage tras un reload). La llamada
      // sigue en el sidebar SOS; solo no aparece como banner flotante.
      const now = Date.now()
      return (this.getBannerQueue || []).filter((c) => {
        const created = c && (c.createdAt || c.created_at)
        if (!created) return true
        const age = now - new Date(created).getTime()
        return Number.isNaN(age) || age <= STALE_MS
      })
    },
    sosCallService() {
      return this.sosCallServiceGetter ? this.sosCallServiceGetter() : null
    },
  },

  watch: {
    // Cada vez que cambia el queue visible, sincroniza los timers de
    // auto-descarte: arranca uno por banner nuevo, limpia los que ya salieron.
    bannerQueue(newVal) {
      this._syncAutoDismissTimers(newVal)
    },
  },

  mounted() {
    // Forzar re-render cada 1s para refrescar "hace Xs"
    this.tickInterval = setInterval(() => { this.nowTick = Date.now() }, 1000)
    // Limpia del queue persistido los banners ya viejos (no deben reaparecer
    // tras un reload). La llamada sigue en el sidebar SOS.
    this._purgeStaleBanners()
    this._syncAutoDismissTimers(this.bannerQueue)
  },

  beforeDestroy() {
    if (this.tickInterval) clearInterval(this.tickInterval)
    if (this._dismissTimers) {
      Object.keys(this._dismissTimers).forEach((id) => clearTimeout(this._dismissTimers[id]))
      this._dismissTimers = {}
    }
  },

  methods: {
    callTitle(call) {
      if (!call) return 'Llamada SOS'
      const ud = call.userData || {}
      const name = [ud.name, ud.lastName1, ud.lastName2].filter(Boolean).join(' ').trim()
      return name || call.name || call.callbackNumber || call.phone || 'Llamada SOS'
    },

    callType(call) {
      const t = call && call.type
      if (t === 'VIDEO_CALL') return 'Videollamada'
      if (t === 'CALL') return 'Audio'
      if (t === 'CHAT') return 'Chat'
      return 'SOS'
    },

    relativeTime(call) {
      // eslint-disable-next-line no-unused-expressions
      this.nowTick
      const created = call && (call.createdAt || call.created_at)
      if (!created) return ''
      const diffMs = Date.now() - new Date(created).getTime()
      if (Number.isNaN(diffMs)) return ''
      const s = Math.floor(diffMs / 1000)
      if (s < 60) return `hace ${s}s`
      const m = Math.floor(s / 60)
      if (m < 60) return `hace ${m}m`
      const h = Math.floor(m / 60)
      return `hace ${h}h`
    },

    callLocation(call) {
      const ud = call && call.userData
      if (ud && ud.address) return ud.address
      const pos = call && call.lastPosition
      if (pos && pos.latitude && pos.longitude) {
        return `${pos.latitude.toFixed(5)}, ${pos.longitude.toFixed(5)}`
      }
      return 'Ubicación pendiente'
    },

    atender(call) {
      if (!this.sosCallService) return
      this.sosCallService.atender(call)
    },

    descartar(call) {
      if (!this.sosCallService) return
      this.sosCallService.descartarBanner(call._id)
    },

    // —— Auto-descarte por tiempo (oculta el banner, NO pierde la llamada) ——
    _syncAutoDismissTimers(queue) {
      if (!this._dismissTimers) this._dismissTimers = {}
      const list = queue || []
      const present = {}
      list.forEach((c) => {
        const id = c && c._id
        if (!id) return
        present[id] = true
        if (!this._dismissTimers[id]) this._scheduleDismiss(id)
      })
      // Limpia timers de banners que ya salieron del queue (atendidos /
      // descartados / expirados).
      Object.keys(this._dismissTimers).forEach((id) => {
        if (!present[id]) {
          clearTimeout(this._dismissTimers[id])
          delete this._dismissTimers[id]
        }
      })
    },

    _scheduleDismiss(id) {
      if (!this._dismissTimers) this._dismissTimers = {}
      this._dismissTimers[id] = setTimeout(() => { this._autoDismiss(id) }, AUTO_DISMISS_MS)
    },

    _autoDismiss(id) {
      if (this._dismissTimers && this._dismissTimers[id]) {
        clearTimeout(this._dismissTimers[id])
        delete this._dismissTimers[id]
      }
      if (this.sosCallService) this.sosCallService.descartarBanner(id)
    },

    // Hover: pausa el auto-descarte mientras el operador lee el banner.
    pauseAutoDismiss(id) {
      if (this._dismissTimers && this._dismissTimers[id]) {
        clearTimeout(this._dismissTimers[id])
        delete this._dismissTimers[id]
      }
    },

    resumeAutoDismiss(id) {
      if (!this._dismissTimers) this._dismissTimers = {}
      // Re-arma solo si el banner sigue visible en el queue.
      const stillVisible = this.bannerQueue.some((c) => c && c._id === id)
      if (stillVisible && !this._dismissTimers[id]) this._scheduleDismiss(id)
    },

    // Quita del queue (y de la persistencia) los banners más viejos que
    // STALE_MS — corre al montar para que los persistidos "hace 2h" no
    // reaparezcan. La llamada sigue en el sidebar SOS.
    _purgeStaleBanners() {
      if (!this.sosCallService) return
      const now = Date.now()
      const all = (this.getBannerQueue || []).slice()
      all.forEach((c) => {
        const created = c && (c.createdAt || c.created_at)
        if (!created) return
        const age = now - new Date(created).getTime()
        if (!Number.isNaN(age) && age > STALE_MS) this.sosCallService.descartarBanner(c._id)
      })
    },
  },
}
</script>

<style scoped>
.sos-banner-stack {
  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  /* Fallback para Safari < 14 sin soporte de min() */
  width: calc(100vw - 48px);
  width: min(720px, calc(100vw - 48px));
  /* z-index sobre el fullscreen del tray y la cámara maximizada (9500)
     para que la alerta de llamada Colibrí sea visible aun cuando el
     operador está viendo una cámara en pantalla completa. */
  z-index: 10000;
  pointer-events: none;
}

.sos-banner {
  position: absolute;
  left: 0;
  right: 0;
  height: 64px;
  background: rgba(208, 72, 72, 0.92);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  color: #FFFFFF;
  display: flex;
  align-items: center;
  padding: 0 16px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.4);
  pointer-events: auto;
}

.sos-banner__icon {
  flex: 0 0 auto;
  margin-right: 12px;
}

.sos-banner__body {
  flex: 1 1 auto;
  min-width: 0;
}

.sos-banner__title {
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sos-banner__meta {
  font-weight: 400;
  opacity: 0.85;
  margin-left: 6px;
}

.sos-banner__subtitle {
  font-size: 13px;
  opacity: 0.85;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sos-banner__actions {
  flex: 0 0 auto;
  margin-left: 12px;
  display: flex;
  align-items: center;
}

.sos-banner-enter-active {
  transition: transform 0.2s ease-out, opacity 0.2s ease-out;
}
.sos-banner-leave-active {
  transition: transform 0.15s ease-in, opacity 0.15s ease-in;
}
.sos-banner-enter {
  transform: translateY(-100%);
  opacity: 0;
}
.sos-banner-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}
</style>
